import React from "react";
import T from "prop-types";
import styled from "styled-components/macro";
import Typography from "commons/components/Typography";
import { Trans, useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useCurrentLocale } from "commons/util/useCurrentLocale";
import { CONTACT_EMAIL_ADDRESS } from "commons/util/constants";

const Container = styled.div`
  height: 100%;
  min-height: 100vh;
  background-color: var(--cream-100);
`;

const Box = styled.div`
  width: 800px;
  max-width: 100%;
  margin: 0 auto;
  padding: 32px;
  color: var(--brown-100);
`;

const Heading = styled(Typography).attrs({ variant: "h4" })`
  margin-top: 32px;
  margin-bottom: 8px;
  font-family: "Stack Sans Headline", sans-serif;
`;

const Paragraph = styled(Typography).attrs({ variant: "paragraph" })`
  margin-bottom: 16px;
`;

const List = styled.ul`
  list-style-type: disc;
  padding-left: 16px;

  & > li {
    font-size: 16px;
    line-height: 26px;
  }
`;

function PrivacyPolicy({ className }) {
  const { t } = useTranslation();
  const currentLocale = useCurrentLocale();

  return (
    <Container>
      <Box className={className}>
        <Typography variant="h2" component="h1" marginTop={24} marginBottom={8}>
          {t("PrivacyPolicy.PrivacyPolicy")}
        </Typography>

        <Paragraph>
          {t("PrivacyPolicy.LastUpdated", {
            date: format(new Date("2026-08-29"), "d MMM y", { locale: currentLocale }),
          })}
        </Paragraph>

        <Paragraph>{t("PrivacyPolicy.Introduction")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section1Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section1Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section2Title")}</Heading>
        <Trans i18nKey="PrivacyPolicy.Section2Text">
          <Paragraph>We use the information we collect to:</Paragraph>
          <List>
            <li>Send you updates about cookyourgoose, including launch news</li>
            <li>Respond to your comments and questions</li>
            <li>Understand and improve how our website performs</li>
            <li>Comply with legal obligations</li>
          </List>
        </Trans>

        <Heading>{t("PrivacyPolicy.Section3Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section3Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section4Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section4Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section5Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section5Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section6Title")}</Heading>
        <Paragraph>
          <Trans i18nKey="PrivacyPolicy.Section6Text" values={{ emailAddress: CONTACT_EMAIL_ADDRESS }}>
            You have the right to access, correct, or delete your personal information. To exercise these rights, please
            contact us at <b>{CONTACT_EMAIL_ADDRESS}</b>.
          </Trans>
        </Paragraph>

        <Heading>{t("PrivacyPolicy.Section7Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section7Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section8Title")}</Heading>
        <Paragraph>{t("PrivacyPolicy.Section8Text")}</Paragraph>

        <Heading>{t("PrivacyPolicy.Section9Title")}</Heading>
        <Paragraph>
          <Trans i18nKey="PrivacyPolicy.Section9Text" values={{ emailAddress: CONTACT_EMAIL_ADDRESS }}>
            If you have any questions about this privacy policy, please contact us at: <b>{CONTACT_EMAIL_ADDRESS}</b>{" "}
            (subject: Privacy Inquiry).
          </Trans>
        </Paragraph>
      </Box>
    </Container>
  );
}

PrivacyPolicy.propTypes = {
  className: T.string,
};

export default PrivacyPolicy;
